import InfoIcon from '@mui/icons-material/Info';
import { Button, CircularProgress, Dialog, Link, Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo } from 'react';

import { GetPitchbookCompanyResponse } from '~/services/rtkQuery/company/types';
import { CompanyStatus } from '~/types/models';
import { formatUrl } from '~/utils/formats';

import styles from './styles.module.scss';

interface Props {
  visible: boolean;
  loading: boolean;
  loadingPitchbookCompanyData: boolean;
  loadingPitchbookCompanyRawDeals: boolean;
  data: GetPitchbookCompanyResponse['result']['data'] | null;
  onClose: () => void;
  onCompanyProfileView: (id: number, status: CompanyStatus | null) => void;
  onCompanyLoad: () => void;
  onRoundsMatch: () => void;
}

const Component: React.FC<Props> = ({
  visible,
  loading,
  loadingPitchbookCompanyData,
  loadingPitchbookCompanyRawDeals,
  data,
  onClose,
  onCompanyProfileView,
  onCompanyLoad,
  onRoundsMatch,
}) => {
  return (
    <Dialog maxWidth="md" open={visible} onClose={onClose}>
      <div className={styles.modal}>
        {data?.latestCompanyId && !loading && (
          <div className={styles.modal__error}>
            <InfoIcon sx={{ color: 'var(--error-color)', mr: '8px' }} />
            <Typography color="var(--error-color)" variant="subtitle1">
              Company already exists
            </Typography>
          </div>
        )}
        <div className={styles.modal__content}>
          <div className={styles.modal__header}>
            <Typography color="text.primary" variant="h5">
              Company Details
            </Typography>
            {!data?.latestCompanyId && !loading && (
              <div>
                <Button
                  disabled={loadingPitchbookCompanyData}
                  loading={loadingPitchbookCompanyRawDeals}
                  size="small"
                  sx={{ backgroundColor: 'var(--main-2-color)' }}
                  onClick={onRoundsMatch}
                >
                  Match Rounds
                </Button>
              </div>
            )}
          </div>
          {loading ? (
            <div className={styles.modal__loading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {' '}
              <div className={styles.modal__info}>
                <div className={styles['modal__info-item']}>
                  <Typography
                    color="text.primary"
                    fontWeight="300"
                    lineHeight="28px"
                    mb="8px"
                    variant="subtitle1"
                  >
                    Company Name
                  </Typography>
                  <Typography
                    color="text.primary"
                    lineHeight="28px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    variant="subtitle1"
                    whiteSpace="nowrap"
                  >
                    {data?.companyname}
                  </Typography>
                </div>
                <div className={styles['modal__info-item']}>
                  <Typography
                    color="text.primary"
                    fontWeight="300"
                    lineHeight="28px"
                    mb="8px"
                    variant="subtitle1"
                  >
                    Website
                  </Typography>
                  <Link
                    noWrap
                    color="rgb(var(--main-2-color-rgb))"
                    display="block"
                    href={formatUrl(data?.website)}
                    lineHeight="28px"
                    overflow="hidden"
                    target="_blank"
                    textOverflow="ellipsis"
                    variant="subtitle1"
                    whiteSpace="nowrap"
                  >
                    {data?.website}
                  </Link>
                </div>
                <div className={styles['modal__info-item']}>
                  <Typography
                    color="text.primary"
                    fontWeight="300"
                    lineHeight="28px"
                    mb="8px"
                    variant="subtitle1"
                  >
                    Pitchbook Link
                  </Typography>
                  <Link
                    noWrap
                    color="rgb(var(--main-2-color-rgb))"
                    display="block"
                    href={formatUrl(data?.pitchBookProfileLink)}
                    lineHeight="28px"
                    overflow="hidden"
                    target="_blank"
                    textOverflow="ellipsis"
                    variant="subtitle1"
                    whiteSpace="nowrap"
                  >
                    {data?.pitchBookProfileLink}
                  </Link>
                </div>
                <div className={styles['modal__info-item']}>
                  <Typography
                    color="text.primary"
                    fontWeight="300"
                    lineHeight="28px"
                    mb="8px"
                    variant="subtitle1"
                  >
                    Source
                  </Typography>
                  <Typography
                    color="text.primary"
                    lineHeight="28px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    variant="subtitle1"
                    whiteSpace="nowrap"
                  >
                    {data?.source === 'internal' ? 'Internal' : 'Pitchbook'}
                  </Typography>
                </div>
                <div className={clsx(styles['modal__info-item'], styles['modal__info-item--bio'])}>
                  <Typography
                    color="text.primary"
                    fontWeight="300"
                    lineHeight="28px"
                    mb="8px"
                    variant="subtitle1"
                  >
                    Description
                  </Typography>
                  <Typography
                    color="text.primary"
                    lineHeight="28px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    variant="subtitle1"
                  >
                    {data?.description}
                  </Typography>
                </div>
              </div>
              <div className={styles['modal__buttons']}>
                <Button
                  color="secondary"
                  disabled={loadingPitchbookCompanyRawDeals || loadingPitchbookCompanyData}
                  sx={{ color: 'var(--black-color)', width: '48%' }}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  disabled={loadingPitchbookCompanyRawDeals}
                  loading={loadingPitchbookCompanyData}
                  sx={{ width: '48%' }}
                  onClick={
                    data?.latestCompanyId
                      ? () => onCompanyProfileView(data.latestCompanyId || 0, data.companyStatus)
                      : onCompanyLoad
                  }
                >
                  {data?.latestCompanyId ? 'View profile' : 'Load & Create Profile'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export const CompanyModal = memo(Component);
